/* eslint-disable no-void */
import React, { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { MdUploadFile } from "react-icons/md";
// eslint-disable-next-line import/no-extraneous-dependencies
import Cropper from "react-easy-crop";
import { Chip, Divider, Slider } from "@mui/material";
import { BiSolidImageAlt } from "react-icons/bi";
import { toast } from "react-toastify";
import * as Styles from "../modal.styles";
import * as S from "./imageCropper.styles";
import { DragNDrop } from "../DragNDrop/dragNDrop.component";
import { MIN_DIMENSION } from "./utils";
import { type FileUploadI } from "./utils";
import { getCroppedImg } from "./exportCroppedImage";
import { SecondaryButton } from "../secondaryButton.component";
import { PrimaryButton } from "../primaryButton.component";

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChange: (file: FileUploadI) => void;
  accept: string;
  shouldCrop?: boolean;
  cropData?: {
    aspectRatio?: number;
    fixedRatio?: boolean;
  };
}

export const FileUploadModal = ({
  isOpen,
  onClose,
  onChange,
  accept,
  shouldCrop,
  cropData,
}: FileUploadModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [imgSrc, setImgSrc] = useState<string>("");
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);
  const [manualAspectRatio, setManualAspectRatio] = useState(
    cropData?.aspectRatio ?? 1
  );

  const [cropping, setCropping] = useState(false);

  useEffect(() => {
    if (cropData?.aspectRatio) {
      setManualAspectRatio(cropData.aspectRatio);
    }
  }, [cropData?.aspectRatio]);

  useEffect(() => {
    setFile(null);
    setImgSrc("");
    setCropping(false);
    setCroppedArea(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, [onClose]);

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedArea(croppedAreaPixels);
  };

  const onSelectFile = (file: File) => {
    if (shouldCrop) {
      if (file.type.startsWith("image")) {
        const reader = new FileReader();
        reader.addEventListener("load", () => {
          const imageElement = new Image();
          const imageUrl = reader.result?.toString() ?? "";
          imageElement.src = imageUrl;
          imageElement.addEventListener("load", (e: Event) => {
            const { naturalWidth, naturalHeight } =
              e.currentTarget as HTMLImageElement;
            if (naturalWidth < MIN_DIMENSION || naturalHeight < MIN_DIMENSION) {
              setImgSrc("");
              setCropping(false);
              toast.error(
                `Imagem deve ter um tamanho de pelo menos ${MIN_DIMENSION}x${MIN_DIMENSION}.`
              );
            } else {
              setImgSrc(imageUrl);
              setCropping(true);
              setFile(file);
            }
          });
        });
        reader.readAsDataURL(file);
      } else {
        setFile(file);
      }
    }
  };

  const handleAspectChange = (newAspectRatio: number) => {
    setManualAspectRatio(newAspectRatio);
  };

  const handleSave = async () => {
    if (!file) {
      toast.error("Arquivo não encontrado");
      return;
    }

    if (shouldCrop && file.type.startsWith("image")) {
      if (!previewCanvasRef.current || !croppedArea) {
        toast.error("Imagem não encontrada");
        return;
      }

      try {
        const croppedImageDataUrl = await getCroppedImg(
          imgSrc,
          croppedArea,
          previewCanvasRef.current
        );
        const newFile: FileUploadI = {
          nome: file.name,
          base64: croppedImageDataUrl as string,
          contentType: file.type,
        };
        onChange(newFile);
        setFile(null);
        setImgSrc("");
      } catch (e) {
        console.error(e);
        toast.error("Erro ao salvar a imagem, por favor tente novamente");
      }
    } else {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        const newFile: FileUploadI = {
          nome: file.name,
          base64: reader.result?.toString() ?? "",
          contentType: file.type,
        };
        onChange(newFile);
        setFile(null);
      });
      reader.readAsDataURL(file);
    }

    onClose();
  };

  return (
    <Styles.DialogContainer open={isOpen} onClose={onClose}>
      <Styles.Container style={{ minWidth: 500 }}>
        <Styles.IconContainer>
          <div className="icon-wrapper">
            <MdUploadFile color="#0000008a" />
          </div>
          <h1 className="title center">
            {shouldCrop && cropping ? "Ajustar imagem" : "Selecionar arquivo"}
          </h1>
          <div style={{ padding: 8 }}>
            <IoClose size={26} className="exit" onClick={onClose} />
          </div>
        </Styles.IconContainer>

        {file && shouldCrop && cropping ? (
          <S.Container>
            {imgSrc && (
              <>
                <S.ImgSrcContainer>
                  <S.CropperContainer>
                    <Cropper
                      image={imgSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={manualAspectRatio ?? 1}
                      onCropChange={setCrop}
                      onCropComplete={onCropComplete}
                      onZoomChange={setZoom}
                      showGrid={false}
                      zoomWithScroll={false}
                    />
                  </S.CropperContainer>
                  <S.AspectsContainer>
                    <Chip
                      label="1:1"
                      variant={manualAspectRatio === 1 ? "filled" : "outlined"}
                      disabled={cropData?.fixedRatio}
                      color="primary"
                      onClick={() => {
                        handleAspectChange(1);
                      }}
                    />
                    <Chip
                      label="16:9"
                      variant={
                        manualAspectRatio === 16 / 9 ? "filled" : "outlined"
                      }
                      disabled={cropData?.fixedRatio}
                      color="primary"
                      onClick={() => {
                        handleAspectChange(16 / 9);
                      }}
                    />
                    <Chip
                      label="3:4"
                      variant={
                        manualAspectRatio === 3 / 4 ? "filled" : "outlined"
                      }
                      disabled={cropData?.fixedRatio}
                      color="primary"
                      onClick={() => {
                        handleAspectChange(3 / 4);
                      }}
                    />
                  </S.AspectsContainer>
                  <S.Slider>
                    <BiSolidImageAlt color="#808080" size={22} />
                    <Slider
                      aria-label="crop-zoom"
                      defaultValue={1}
                      step={0.1}
                      min={1}
                      max={3}
                      onChange={(e: any) => {
                        setZoom(e.target.value as number);
                      }}
                      color="primary"
                      sx={{ ".MuiSlider-rail": { backgroundColor: "#CCC" } }}
                    />
                    <BiSolidImageAlt color="#808080" size={36} />
                  </S.Slider>
                </S.ImgSrcContainer>
                <Divider />
              </>
            )}
            {crop && (
              <canvas
                ref={previewCanvasRef}
                style={{
                  display: "none",
                  marginTop: 12,
                  border: "1px solid #ccc",
                  objectFit: "contain",
                  maxWidth: "50%",
                }}
              />
            )}
          </S.Container>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              width: "100%",
            }}
          >
            <DragNDrop
              onFilesSelected={onSelectFile}
              accept={accept}
              onRemoveFile={() => {
                setFile(null);
              }}
              onChangeManual={onSelectFile}
            />
          </div>
        )}

        <Styles.Buttons style={{ marginTop: 10 }}>
          <SecondaryButton content={"Cancelar"} onClick={onClose} />
          <PrimaryButton
            content={"Salvar"}
            disabled={!file || (shouldCrop && cropping && !imgSrc)}
            onClick={() => {
              void handleSave();
            }}
          />
        </Styles.Buttons>
      </Styles.Container>
    </Styles.DialogContainer>
  );
};
